import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X, LogOut, ShieldCheck, User as UserIcon } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Home", id: "home" },
  { to: "/store", label: "Store", id: "store" },
  { to: "/gallery", label: "Gallery", id: "gallery" },
  { to: "/devotions", label: "Devotions", id: "devotions" },
  { to: "/about", label: "About", id: "about" },
  { to: "/donate", label: "Donate", id: "donate" },
  { to: "/join", label: "Join Now", id: "join" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-obsidian/80 border-b border-gold/15" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo-link" aria-label="Crusaders of the Cross home">
          <LogoFull />
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="main-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.id}
              to={n.to}
              data-testid={`nav-${n.id}-link`}
              className={({ isActive }) =>
                `nav-link font-condensed uppercase tracking-[0.2em] text-sm transition-colors duration-300 hover:text-gold ${isActive ? "text-gold active" : "text-stone-300"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          {user && (
            <NavLink
              to="/members"
              data-testid="nav-members-link"
              className={({ isActive }) =>
                `nav-link font-condensed uppercase tracking-[0.2em] text-sm transition-colors duration-300 hover:text-gold ${isActive ? "text-gold active" : "text-stone-300"}`
              }
            >
              Members HQ
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="cart-open-button"
            onClick={() => setOpen(true)}
            className="relative p-2.5 text-stone-300 hover:text-gold transition-colors duration-300"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-gold text-obsidian font-mono text-[10px] font-semibold w-4.5 h-4.5 min-w-[18px] min-h-[18px] rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-button" className="hidden md:flex items-center gap-2 border border-gold/30 hover:border-gold/70 transition-colors duration-300 px-4 py-2 font-condensed uppercase tracking-[0.15em] text-sm text-parchment">
                  <UserIcon className="w-4 h-4 text-gold" />
                  {user.name.split(" ")[0]}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate2 border-gold/25 text-parchment">
                <DropdownMenuItem data-testid="menu-members-link" onClick={() => navigate("/members")} className="cursor-pointer focus:bg-gold/15 focus:text-gold">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Members HQ
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin-link" onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-crimson/20 focus:text-crimson">
                    <ShieldCheck className="w-4 h-4 mr-2" /> Command Post
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-logout-button" onClick={logout} className="cursor-pointer focus:bg-crimson/20 focus:text-crimson">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login-link"
              className="hidden md:inline-flex gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.18em] text-sm font-semibold px-5 py-2.5 hover:bg-gold-light hover:shadow-[0_0_24px_rgba(212,175,55,0.35)] transition-[background-color,box-shadow] duration-300"
            >
              Member Login
            </Link>
          )}

          <button
            data-testid="mobile-menu-button"
            className="md:hidden p-2 text-stone-300 hover:text-gold transition-colors"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden border-t border-gold/15 bg-obsidian/95"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink key={n.id} to={n.to} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${n.id}-link`} className="font-condensed uppercase tracking-[0.2em] text-lg text-stone-200 hover:text-gold">
                  {n.label}
                </NavLink>
              ))}
              {user ? (
                <>
                  <NavLink to="/members" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-members-link" className="font-condensed uppercase tracking-[0.2em] text-lg text-gold">Members HQ</NavLink>
                  {user.role === "admin" && (
                    <NavLink to="/admin" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-admin-link" className="font-condensed uppercase tracking-[0.2em] text-lg text-crimson">Command Post</NavLink>
                  )}
                  <button data-testid="mobile-logout-button" onClick={() => { logout(); setMobileOpen(false); }} className="text-left font-condensed uppercase tracking-[0.2em] text-lg text-crimson">Sign Out</button>
                </>
              ) : (
                <NavLink to="/login" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-login-link" className="font-condensed uppercase tracking-[0.2em] text-lg text-gold">Member Login</NavLink>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
